/**
 * Parser de mensajes de WhatsApp para registro de clientes nuevos.
 *
 * Caso de uso: el cliente nuevo escribe por WhatsApp y responde el
 * formato de registro (Nombre / Dirección / Ubicación GPS). El
 * encargado pega el mensaje completo en /nuevo-cliente y este parser
 * extrae los campos para autollenar el formulario.
 *
 * Soporta:
 * - El formato lleno en un solo mensaje
 * - Los datos en mensajes separados (se pegan juntos)
 * - Links largos de Google Maps (@lat,lng / !3d!4d / ?q=lat,lng)
 * - Links cortos (maps.app.goo.gl / goo.gl) → se devuelven en
 *   shortMapsUrl para que el caller los expanda vía API y vuelva a
 *   extraer las coordenadas de la URL larga.
 * - Coordenadas sueltas "7.7665, -72.2287"
 */

// Regex de teléfono venezolano — la misma de order-detail.js
// (códigos móviles válidos: 412, 414, 416, 424, 426)
const REGEX_PHONE_VE = /(?:\+?58|0)?[\s-]?(?:4(?:12|14|16|24|26))[\s-]?\d{3}[\s-]?\d{4}/;

// Links acortados que requieren expansión (sin coordenadas visibles).
// El path puede traer slashes (goo.gl/maps/abc123)
const REGEX_SHORT_MAPS_URL = /https?:\/\/(?:www\.)?(?:maps\.app\.goo\.gl|goo\.gl)\/[\w/-]+/i;

/**
 * Devuelve el link corto de Google Maps presente en un texto, o null.
 * @param {string} text
 * @returns {string|null}
 */
export const detectShortMapsUrl = (text) => {
    if (!text) return null;
    const match = text.match(REGEX_SHORT_MAPS_URL);
    return match ? match[0] : null;
};

/**
 * Normaliza un teléfono venezolano al formato de la DB (58XXXXXXXXXX).
 * Acepta: "+58 412-1231127", "0414-7353821", "4121231127", "584121231127".
 * @param {string} input
 * @returns {string|null} — null si no hay entrada; devuelve solo dígitos
 *   como mejor esfuerzo si el patrón no es reconocido (no bloquea el guardado)
 */
export const normalizePhoneVE = (input) => {
    if (!input) return null;
    let digits = String(input).replace(/\D/g, '');
    if (!digits) return null;

    if (digits.startsWith('0058')) digits = digits.slice(2);

    let normalized = null;
    if (digits.length === 12 && digits.startsWith('58')) {
        normalized = digits;
    } else if (digits.length === 11 && digits.startsWith('0')) {
        // 0414… → 58414… (reemplazo del cero inicial por el código de área)
        normalized = `58${digits.slice(1)}`;
    } else if (digits.length === 10 && /^4(?:12|14|16|24|26)/.test(digits)) {
        normalized = `58${digits}`;
    }

    // Código móvil válido: 412, 414, 416, 424, 426
    if (normalized && /^58(?:412|414|416|424|426)\d{7}$/.test(normalized)) {
        return normalized;
    }

    // Formato no reconocido: mejor esfuerzo (solo dígitos), no bloquea el guardado
    return digits;
};

// Rango de sanidad para coordenadas (Venezuela y frontera con Colombia)
const isSanityCoords = (lat, lng) => lat >= 0 && lat <= 12.5 && lng >= -79 && lng <= -59;

/**
 * Extrae lat/lng de un texto: URL larga de Google Maps o par suelto.
 * @param {string} text
 * @returns {{ lat: number|null, lng: number|null }}
 */
export const extractCoords = (text) => {
    if (!text) return { lat: null, lng: null };

    const patterns = [
        /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/, // ...!3d7.7732!4d-72.2186...
        /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/, // .../@7.7732,-72.2186,...
        /[?&]q=(-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)/, // ...?q=7.7732,-72.2186
        /(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/, // 7.7732, -72.2186 (suelto)
    ];

    for (const re of patterns) {
        const match = text.match(re);
        if (match) {
            const lat = parseFloat(match[1]);
            const lng = parseFloat(match[2]);
            if (isSanityCoords(lat, lng)) return { lat, lng };
        }
    }
    return { lat: null, lng: null };
};

// Quita los asteriscos del formato en negrita de WhatsApp y espacios
const cleanLine = (line) => line.replace(/\*/g, '').trim();

// Etiquetas del formato de registro. 'ignore' = sección que se salta
// (correo: ya no se pide al cliente; teléfono: se detecta aparte)
const LABELS = [
    { key: 'name', re: /^nombre(?:\s+y\s+apellido)?\s*:?\s*/i },
    { key: 'address', re: /^direcci[oó]n(?:\s+de\s+entrega)?\s*:?\s*/i },
    { key: 'gps', re: /^(?:ubicaci[oó]n(?:\s+gps)?|gps|location)\s*(?:\(opcional\))?\s*:?\s*/i },
    {
        key: 'ignore',
        re: /^(?:correo(?:\s+electr[oó]nico)?|email|tel[eé]fono(?:\s+de\s+quien\s+recibe)?)\s*:?\s*/i,
    },
];

const matchLabel = (line) => {
    const clean = cleanLine(line);
    for (const label of LABELS) {
        const match = clean.match(label.re);
        if (match) {
            return { key: label.key, rest: clean.slice(match[0].length).trim() };
        }
    }
    return null;
};

// Líneas que son GPS puro (coordenadas o link de Maps), no contenido de
// una sección: cuando el GPS llega en un mensaje aparte sin etiqueta,
// no debe colarse en la dirección ni en el nombre.
const isGpsOnlyLine = (clean) =>
    /^-?\d{1,2}\.\d+\s*,\s*-?\d{1,3}\.\d+$/.test(clean) ||
    (/^https?:\/\/\S+$/i.test(clean) && /maps|goo\.gl|google/i.test(clean));

// Segmentos de dirección que pertenecen a street2 (casa, edificio, piso...)
const MARKER_STREET2 = /\b(casa|edificio|piso|apto|apartamento|local)\b|#/i;

/**
 * Divide una dirección completa en street1 (sector/avenida/calle) y
 * street2 (casa/edificio/piso/apto), buscando el primer segmento
 * separado por coma que contenga un marcador de street2.
 */
const splitAddress = (full) => {
    const segments = full.split(',');
    for (let i = 1; i < segments.length; i++) {
        if (MARKER_STREET2.test(segments[i])) {
            return {
                street1: segments.slice(0, i).join(',').trim(),
                street2: segments.slice(i).join(',').trim(),
            };
        }
    }
    return { street1: full, street2: null };
};

/**
 * Parsea el texto pegado del chat de WhatsApp.
 * @param {string} rawText — uno o varios mensajes pegados juntos
 * @returns {{ name, phone, street1, street2, lat, lng, shortMapsUrl }}
 */
export const parseWhatsAppRegistro = (rawText) => {
    const result = {
        name: null,
        phone: null,
        street1: null,
        street2: null,
        lat: null,
        lng: null,
        shortMapsUrl: null,
    };
    if (!rawText) return result;

    // Link corto de Maps → el caller debe expandirlo antes de tener coords
    const shortMatch = rawText.match(REGEX_SHORT_MAPS_URL);
    result.shortMapsUrl = shortMatch ? shortMatch[0] : null;

    // Coordenadas de URL larga o par suelto
    const coords = extractCoords(rawText);
    result.lat = coords.lat;
    result.lng = coords.lng;

    // Secciones por etiqueta
    const sections = {};
    let current = null;
    for (const line of rawText.split(/\r?\n/)) {
        const label = matchLabel(line);
        if (label) {
            current = label.key === 'ignore' ? null : label.key;
            if (label.rest && current) {
                sections[current] = sections[current] || [];
                sections[current].push(label.rest);
            }
            continue;
        }
        const clean = cleanLine(line);
        if (!clean || !current || isGpsOnlyLine(clean)) continue;
        sections[current] = sections[current] || [];
        sections[current].push(clean);
    }

    if (sections.name && sections.name.length) {
        result.name = sections.name.join(' ').trim() || null;
    }

    if (sections.address && sections.address.length) {
        const full = sections.address.join(', ').trim();
        const split = splitAddress(full);
        result.street1 = split.street1 || null;
        result.street2 = split.street2 || null;
    }

    // Teléfono venezolano en cualquier parte del texto
    const phoneMatch = rawText.match(REGEX_PHONE_VE);
    if (phoneMatch) {
        result.phone = phoneMatch[0].replace(/[^\d+]/g, '');
    }

    return result;
};

/**
 * Genera el correo automático del cliente:
 * "Pedro Fernandez" → "pedrofernandez@aguamandelivery.com"
 * Normaliza: minúsculas, sin acentos, solo letras.
 * @param {string} name
 * @param {string} [suffix] — opcional: dígitos del teléfono anti-colisión
 *   (dos clientes con el mismo nombre completo) → "pedrofernandez3538@…"
 * @returns {string|null} — null si el nombre no genera base válida
 */
export const generateClientEmail = (name, suffix = '') => {
    if (!name) return null;
    const base = name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
    if (!base) return null;
    return `${base}${suffix}@aguamandelivery.com`;
};
